import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import '../backlog/backlog.css';
import './sampleTransferDetail.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar, ExportMenu } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { DEFAULT_DATE_RANGE } from '../../components/listDefaults';
import { DateRangeSelect, TRACKING_RANGES } from '../modules/DateRangeSelect';
import { useNotify } from '../../components/notifications';
import { runLoading } from '../../components/loading';
import SampleTransferDetailPage from './SampleTransferDetailPage';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface SampleTransferRow {
  rowKey:            string;
  jobOid:            string;
  jobId:             string;
  client:            string;
  projectId:         string;
  projectName:       string;
  testSummary:       string;
  sampleMatrix:      string;
  status:            string;
  statusId:          string;
  samplingStatusInt: number;
  testCount:         number;
  receivedDate:      string | null;
  dueDate:           string | null;
  updatedDate:       string | null;
}

type StatusFilter =
  | 'All'
  | 'Submitted'
  | 'Submitted, Pending Field Data Entry'
  | 'Pending Transfer'
  | 'Pending Submission, Field Data not Submitted'
  | 'Pending Submission, Field Submitted';
type DateRangeKey = '1M' | '3M' | '6M' | '1Y' | '2Y' | '5Y' | 'All';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
function cutoffDate(key: DateRangeKey): Date | null {
  if (key === 'All') return null;
  const now = new Date();
  const map: Record<Exclude<DateRangeKey, 'All'>, () => Date> = {
    '1M': () => new Date(now.getFullYear(), now.getMonth() - 1,  now.getDate()),
    '3M': () => new Date(now.getFullYear(), now.getMonth() - 3,  now.getDate()),
    '6M': () => new Date(now.getFullYear(), now.getMonth() - 6,  now.getDate()),
    '1Y': () => new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()),
    '2Y': () => new Date(now.getFullYear() - 2, now.getMonth(), now.getDate()),
    '5Y': () => new Date(now.getFullYear() - 5, now.getMonth(), now.getDate()),
  };
  return map[key as Exclude<DateRangeKey, 'All'>]();
}

// Status → shared pill variant (backlog.css .bl-pill ok/warn/alert/info) —
// the page's original severity semantics, rendered through the shared pill
// classes so the Status column is the ONLY coloured cell (BO UI Standard).
function statusPillVariant(statusId: string): string {
  if (statusId === 'ST-1-5')                          return 'ok';     // fully submitted
  if (statusId === 'ST-1-6' || statusId === 'ST-1-3') return 'info';   // submitted but FD pending
  if (statusId === 'ST-1-1' || statusId === 'ST-1-4') return 'warn';   // pending transfer
  if (statusId === 'ST-1-2')                          return 'alert';  // pending transfer + no FD
  return 'info';
}


// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
export default function SampleTransferPage() {
  const [rows,       setRows]       = useState<SampleTransferRow[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [loadMs,     setLoadMs]     = useState<number | null>(null);
  const [error,      setError]      = useState<string | null>(null);
  const [dateRange,  setDateRange]  = useState<DateRangeKey>(DEFAULT_DATE_RANGE.SampleTransfer as DateRangeKey);
  const [statusFilt, setStatusFilt] = useState<StatusFilter>('All');
  const [search,     setSearch]     = useState('');
  const [selectedJob, setSelectedJob] = useState<{ rowKey: string; jobId: string } | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const [reportItems, setReportItems] = useState<{ id: number; label: string; templateName: string; allowMultipleJobId: boolean }[]>([]);
  const [reportsOpen, setReportsOpen] = useState(false);
  // List grid Maximize (icons live on the status-filter row, not a separate grid
  // header strip — user request 2026-09-22). Esc restores from full screen.
  const [listMax, setListMax] = useState(false);
  useEffect(() => {
    if (!listMax) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setListMax(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [listMax]);
  const notify     = useNotify();
  const searchRef  = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const t0 = performance.now();
    try {
      const params: Record<string, string> = {};
      const co = cutoffDate(dateRange);
      if (co) {
        const pad = (n: number) => String(n).padStart(2, '0');
        params.startDate = `${co.getFullYear()}-${pad(co.getMonth() + 1)}-${pad(co.getDate())}`;
        params.endDate   = `${new Date().getFullYear()}-${pad(new Date().getMonth() + 1)}-${pad(new Date().getDate())}`;
      }
      const res = await api.get('/api/v1/sample-transfer', { params });
      setRows((res.data as { data: SampleTransferRow[] }).data ?? []);
      setSelectedIds(new Set());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => { void load(); }, [load]);

  // Reports ▾ menu — driven by GoLive Form Report Permissions for this view.
  useEffect(() => {
    void (async () => {
      try {
        const res = await api.get('/api/v1/sample-transfer/reports');
        setReportItems((res.data as { data?: typeof reportItems }).data ?? []);
      } catch { /* no reports assigned → dropdown stays hidden (GoLive parity) */ }
    })();
  }, []);

  // ── Derived data ───────────────────────────────────────────────────────────
  // Status chips + global search filter here; AppDataGrid does per-column
  // filter, sort and pagination.
  const filtered = useMemo(() => {
    let r = rows;
    if (statusFilt === 'Submitted')                            r = r.filter(x => x.statusId === 'ST-1-5');
    else if (statusFilt === 'Submitted, Pending Field Data Entry') r = r.filter(x => x.statusId === 'ST-1-6');
    else if (statusFilt === 'Pending Transfer')                r = r.filter(x => x.statusId === 'ST-1-1' || x.statusId === 'ST-1-2');
    else if (statusFilt === 'Pending Submission, Field Data not Submitted') r = r.filter(x => x.statusId === 'ST-1-4');
    else if (statusFilt === 'Pending Submission, Field Submitted')          r = r.filter(x => x.statusId === 'ST-1-3');
    if (search.trim()) {
      const t = search.toLowerCase();
      r = r.filter(x =>
        x.jobId.toLowerCase().includes(t) ||
        x.client.toLowerCase().includes(t) ||
        x.projectId.toLowerCase().includes(t) ||
        x.projectName.toLowerCase().includes(t) ||
        x.testSummary.toLowerCase().includes(t) ||
        x.status.toLowerCase().includes(t) ||
        x.statusId.toLowerCase().includes(t)
      );
    }
    return r;
  }, [rows, statusFilt, search]);

  const submittedCount    = useMemo(() => rows.filter(x => x.statusId === 'ST-1-5').length, [rows]);
  const pendingFdCount    = useMemo(() => rows.filter(x => x.statusId === 'ST-1-6').length, [rows]);
  const pendingTransfer   = useMemo(() => rows.filter(x => x.statusId === 'ST-1-1' || x.statusId === 'ST-1-2').length, [rows]);
  const pendSubNoFdCount  = useMemo(() => rows.filter(x => x.statusId === 'ST-1-4').length, [rows]);
  const pendSubFdCount    = useMemo(() => rows.filter(x => x.statusId === 'ST-1-3').length, [rows]);
  const submittedPct      = rows.length ? Math.round(submittedCount / rows.length * 100) : 0;
  const distinctJobs      = useMemo(() => new Set(rows.map(r => r.jobId)).size, [rows]);

  // Reports ▾ → render the chosen template for the ticked rows' distinct jobs.
  const runReport = useCallback(async (it: { templateName: string; label: string }) => {
    setReportsOpen(false);
    const jobIds = Array.from(new Set(
      rows.filter(r => selectedIds.has(r.rowKey)).map(r => r.jobId),
    ));
    if (jobIds.length === 0) {
      notify.warning('Please select at least one record to preview the report.');
      return;
    }
    try {
      const res = await runLoading(
        api.get('/api/v1/sample-transfer/report-pdf',
          { params: { templateName: it.templateName, jobIds: jobIds.join(',') }, responseType: 'blob' }),
        { blocking: true, label: `Generating ${it.label}…` });
      const blob = res.data as Blob;
      if (res.status === 204 || !blob || blob.size === 0) {
        notify.warning(`No data to render for ${it.label}.`);
        return;
      }
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      notify.error(`Failed to generate ${it.label}.`);
    }
  }, [rows, selectedIds, notify]);

  // Shared-grid columns.  Status keeps its original hex-coloured text via render.
  const cols: AppColumn<SampleTransferRow>[] = [
    { field: 'jobId',       header: 'JobID',       width: 110 },
    { field: 'client',      header: 'Client',      width: 170 },
    { field: 'projectId',   header: 'ProjectID',   width: 160 },
    { field: 'projectName', header: 'ProjectName', width: 200 },
    { field: 'testSummary', header: 'TestSummary', width: 240 },
    { field: 'status', header: 'Status', width: 230,
      text: r => r.status,
      render: r => <span className={`bl-pill ${statusPillVariant(r.statusId)}`}>{r.status}</span> },
    { field: 'statusId', header: 'StatusID', width: 90 },
  ];

  // ── Master → detail ─────────────────────────────────────────────────────────
  if (selectedJob) {
    return (
      <SampleTransferDetailPage
        rowKey={selectedJob.rowKey}
        jobId={selectedJob.jobId}
        onBack={() => { setSelectedJob(null); void load(); }}
      />
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="bl-page">
        <div className="bl-top">
          <div className="dp-page-hdr">
            <div className="dp-breadcrumb">
              <span>Home</span>
              <i className="ti ti-chevron-right" />
              <span>Sampling Management</span>
              <i className="ti ti-chevron-right" />
              <span className="dp-bc-active">Sample Transfer</span>
            </div>
            {/* Shared FormTitle — the ONE page heading (§18.6): nav icon/colour
                via navMeta + the TOP load-time chip. */}
            <div className="dp-title-row">
              <FormTitle pageId="samptransfer" auditFormName="Sample Transfer" loadMs={loadMs} loading={loading} />
            </div>
            <div className="dp-subtitle">
              Read-only · {loading ? '…' : rows.length} items · {dateRange}
            </div>
          </div>

          <div className="kpi-row">
            <div className="kpi">
              <div className="kpi-lbl">Total ({dateRange})</div>
              <div className="kpi-val kpi-blue">{loading ? '…' : rows.length}</div>
              <div className="kpi-chg">Items in range</div>
            </div>
            <div className="kpi">
              <div className="kpi-lbl">Submitted</div>
              <div className="kpi-val kpi-teal">{loading ? '…' : submittedCount}</div>
              <div className="kpi-chg" style={{ color: 'var(--ai-green)' }}>{submittedPct}%</div>
            </div>
            <div className="kpi">
              <div className="kpi-lbl">Pending FD Entry</div>
              <div className="kpi-val" style={{ color: 'var(--ai-blue)' }}>{loading ? '…' : pendingFdCount}</div>
              <div className="kpi-chg">ST-1-6</div>
            </div>
            <div className="kpi">
              <div className="kpi-lbl">Pending Transfer</div>
              <div className="kpi-val kpi-red">{loading ? '…' : pendingTransfer}</div>
              <div className="kpi-chg" style={{ color: 'var(--ai-amber)' }}>{distinctJobs} distinct jobs</div>
            </div>
          </div>
        </div>

        {/* Grid area: toolbar + search sit ON the grey page (BO UI Standard v2);
            only the grid card below is white. */}
        <div className="bl-grid-area">
          {/* Toolbar on its OWN full-width row — canonical order: [left] ·
              Refresh · Export to.  This read grid has no New/Edit/Delete; the
              date filter rides in the left slot (shared .sa-date-* chip). */}
          <div style={{ margin: '0 0 8px' }}>
            <AppToolbar
              left={
                <>
                  <DateRangeSelect variant="dropdown" label="Date Range" ranges={TRACKING_RANGES}
                    value={TRACKING_RANGES.find(r => r.label === dateRange)?.days ?? null}
                    onChange={(d) => setDateRange((TRACKING_RANGES.find(r => r.days === d)?.label ?? 'All') as DateRangeKey)} />

                  {/* Reports ▾ (GoLive Reports dropdown) — DB-driven; acts on ticked rows. */}
                  {reportItems.length > 0 && (
                    <span className="app-toolbar__export">
                      <button className="ca-btn" onClick={() => setReportsOpen(o => !o)}
                              disabled={selectedIds.size === 0}
                              title={selectedIds.size === 0 ? 'Select at least one row' : 'Reports'}>
                        <span className="tb-i tb-i-blue"><i className="ti ti-file-text" /></span> Reports ▾
                      </button>
                      {reportsOpen && (
                        <>
                          <div className="app-toolbar__export-backdrop" onClick={() => setReportsOpen(false)} />
                          <div className="app-toolbar__export-menu" role="menu">
                            {reportItems.map(it => (
                              <button key={it.id} className="app-toolbar__export-item" role="menuitem"
                                      onClick={() => runReport(it)}>
                                <i className="ti ti-file-text" /> {it.label}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </span>
                  )}
                </>
              }
              onRefresh={load}
              refreshing={loading}
              exportData={{ filename: 'sample-transfer', ...gridExportMatrix(filtered, cols) }}
            />
          </div>

          {/* Search — shared .list-search-row on the grey page, never inside the card. */}
          <div className="list-search-row">
            <div className="list-search">
              <span className="tb-i">🔍</span>
              <input
                ref={searchRef}
                type="text"
                placeholder="Search job ID, client, project, test, status…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && (
                <span className="list-search-clear" onClick={() => setSearch('')}>×</span>
              )}
            </div>
            {(filtered.length !== rows.length) && (
              <span className="search-match-count">
                {filtered.length} match{filtered.length !== 1 ? 'es' : ''}
              </span>
            )}
          </div>

          {/* ONE white grid card — filter-chip strip + grid + its single footer. */}
          <div className="bl-card bl-card-flex">

            {/* Status filter — shared .sa-filter-* chips (backlog.css) */}
            <div className="sa-filter-row">
              <span className="sa-filter-lbl">Status:</span>
              {([
                { id: 'All',                                   count: rows.length },
                { id: 'Submitted',                             count: submittedCount },
                { id: 'Submitted, Pending Field Data Entry',   count: pendingFdCount },
                { id: 'Pending Transfer',                      count: pendingTransfer },
                { id: 'Pending Submission, Field Data not Submitted', count: pendSubNoFdCount },
                { id: 'Pending Submission, Field Submitted',   count: pendSubFdCount },
              ] as { id: StatusFilter; count: number }[]).map(t => (
                <button
                  key={t.id}
                  className={`sa-filter-btn${statusFilt === t.id ? ' active' : ''}`}
                  onClick={() => setStatusFilt(t.id)}
                >
                  {t.id} ({t.count})
                </button>
              ))}
              {/* Export + Maximize — on THIS (status-filter) row, not a separate grid
                  header strip (user request 2026-09-22). Same icons/size as SDMS. */}
              <span className="stx-listacts">
                <ExportMenu
                  iconOnly
                  triggerClassName="adg-max-btn"
                  filename="sample-transfer"
                  {...gridExportMatrix(filtered, cols)}
                />
                <button
                  type="button"
                  className="adg-max-btn"
                  title="Maximize"
                  aria-label="Maximize"
                  onClick={() => setListMax(true)}
                >
                  <i className="ti ti-arrows-maximize" aria-hidden="true" />
                </button>
              </span>
            </div>

            {error ? (
              <div className="bl-err" style={{ margin: 12 }}>
                <i className="ti ti-alert-circle" /> {error}
                <button onClick={load} style={{ marginLeft: 10, color: 'var(--brand-accent, #e07b39)', border: 'none', background: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Retry</button>
              </div>
            ) : (
              /* Shared data grid — styling, per-column filters, sort, footer /
                 pagination and load-time are all standardised in <AppDataGrid>. */
              <AppDataGrid<SampleTransferRow>
                rows={filtered}
                columns={cols}
                getRowId={r => r.rowKey}
                /* Export + Maximize live on THIS form's status-filter row (above),
                   so suppress the grid's own header cluster — else it shows twice
                   (user report 2026-09-23). */
                hideHeaderTools
                selectable
                selectedIds={selectedIds}
                onSelectionChange={setSelectedIds}
                onRowClick={r => setSelectedJob({ rowKey: r.rowKey, jobId: r.jobId })}
                className={listMax ? 'adg-maximized stx-list-maxed' : undefined}
                loading={loading}
                loadMs={loadMs}
                emptyText="No records found"
              />
            )}
            {/* Top bar for the maximized grid — holds the Restore control on its OWN
                row so it never overlaps the grid's column headers (user report
                2026-09-22). The grid is offset below it via .stx-list-maxed. */}
            {listMax && (
              <div className="stx-list-maxbar">
                <button
                  type="button"
                  className="adg-max-btn"
                  title="Restore (Esc)"
                  aria-label="Restore"
                  onClick={() => setListMax(false)}
                >
                  <i className="ti ti-arrows-minimize" aria-hidden="true" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
